<template>
  <form class="ds-container ds-container--wide" @submit.prevent="save">
    <div class="ds-form-grid">
      <!-- Main column -->
      <div>
        <div class="form-card">
          <div class="form-card__header">
            <div class="form-card__header-icon"><i class="pi pi-id-card"></i></div>
            <div>
              <p class="form-card__title">بيانات طريقة الدفع</p>
              <p class="form-card__subtitle">الاسم وتفاصيل الحساب التي يراها الطالب</p>
            </div>
          </div>
          <div class="form-card__body">
            <div class="ds-field-grid">
              <div class="form-field ds-field--full">
                <label class="field-label">الاسم <span class="required">*</span></label>
                <input type="text" class="form-control" v-model="form.name" placeholder="مثال: إنستا باي" />
              </div>
              <div class="form-field">
                <label class="field-label">اسم الحساب <span class="optional">اختياري</span></label>
                <input type="text" class="form-control" v-model="form.account_name" placeholder="اسم صاحب الحساب" />
              </div>
              <div class="form-field">
                <label class="field-label">رقم الحساب / المحفظة <span class="optional">اختياري</span></label>
                <input type="text" class="form-control" v-model="form.account_number" placeholder="01xxxxxxxxx" dir="ltr" />
              </div>
              <div class="form-field ds-field--full">
                <label class="field-label">IBAN <span class="optional">اختياري</span></label>
                <input type="text" class="form-control" v-model="form.iban" placeholder="EGxx xxxx xxxx xxxx" dir="ltr" />
              </div>
              <div class="form-field ds-field--full">
                <label class="field-label">تعليمات الدفع <span class="optional">اختياري</span></label>
                <textarea class="form-control" rows="4" v-model="form.instructions" placeholder="خطوات إرسال المبلغ التي تظهر للطالب..."></textarea>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Sidebar column -->
      <div>
        <div class="form-card">
          <div class="form-card__header">
            <div class="form-card__header-icon"><i class="pi pi-image"></i></div>
            <p class="form-card__title">الوسائط</p>
          </div>
          <div class="form-card__body">
            <div class="form-field mb-3">
              <label class="field-label">الشعار <span class="optional">اختياري</span></label>
              <label :class="['upload-zone', 'upload-zone--image', { 'upload-zone--has-file': preview.image }]">
                <template v-if="preview.image">
                  <img :src="preview.image" class="upload-zone__preview" alt="" />
                  <div class="upload-zone__overlay"><i class="pi pi-camera"></i> تغيير</div>
                </template>
                <template v-else>
                  <div class="upload-zone__icon"><i class="pi pi-cloud-upload"></i></div>
                  <p class="upload-zone__label">اضغط لاختيار صورة</p>
                </template>
                <input type="file" accept="image/*" @change="onImage($event, 'image')" />
              </label>
            </div>
            <div class="form-field">
              <label class="field-label">صورة QR <span class="optional">اختياري</span></label>
              <label :class="['upload-zone', 'upload-zone--image', { 'upload-zone--has-file': preview.qr_image }]">
                <template v-if="preview.qr_image">
                  <img :src="preview.qr_image" class="upload-zone__preview" alt="" />
                  <div class="upload-zone__overlay"><i class="pi pi-camera"></i> تغيير</div>
                </template>
                <template v-else>
                  <div class="upload-zone__icon"><i class="pi pi-qrcode"></i></div>
                  <p class="upload-zone__label">اضغط لاختيار صورة</p>
                </template>
                <input type="file" accept="image/*" @change="onImage($event, 'qr_image')" />
              </label>
            </div>
          </div>
        </div>

        <div class="form-card">
          <div class="form-card__header">
            <div class="form-card__header-icon"><i class="pi pi-sliders-h"></i></div>
            <p class="form-card__title">الإعدادات</p>
          </div>
          <div class="form-card__body">
            <label class="settings-row">
              <div class="settings-row__text">
                <span class="settings-row__title">مفعّل</span>
                <span class="settings-row__desc">متاح للطالب عند الاشتراك</span>
              </div>
              <InputSwitch v-model="form.is_active" />
            </label>
            <div class="form-field mt-3">
              <label class="field-label">الترتيب <span class="optional">اختياري</span></label>
              <input type="number" class="form-control" v-model="form.sort_order" min="0" placeholder="0" dir="ltr" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="form-actions form-actions--sticky">
      <button type="submit" class="btn-submit" :disabled="store.saving || !form.name.trim()">
        <i class="pi pi-spin pi-spinner-dotted" v-if="store.saving"></i>
        <i class="pi pi-check" v-else></i>
        {{ store.saving ? 'جارٍ الحفظ...' : 'حفظ طريقة الدفع' }}
      </button>
      <button type="button" class="btn-cancel" @click="$router.push('/payment-methods')">إلغاء</button>
    </div>
  </form>
</template>

<script>
import InputSwitch from 'primevue/inputswitch';
import Swal from 'sweetalert2';
import { usePaymentMethodStore } from '@/stores/usePaymentMethodStore.js';
import { apiError } from '@/composables/subscriptionMeta.js';

function emptyForm() {
  return { name: '', account_name: '', account_number: '', iban: '', instructions: '', sort_order: 0, is_active: true };
}

export default {
  name: 'PaymentMethodForm',
  components: { InputSwitch },
  props: {
    method: { type: Object, default: null },
  },
  data() {
    return {
      form: emptyForm(),
      files: { image: null, qr_image: null },
      preview: { image: null, qr_image: null },
    };
  },
  computed: {
    store() { return usePaymentMethodStore(); },
  },
  watch: {
    method: {
      immediate: true,
      handler(m) {
        if (!m) return;
        this.form = {
          name: m.name ?? '', account_name: m.account_name ?? '', account_number: m.account_number ?? '',
          iban: m.iban ?? '', instructions: m.instructions ?? '', sort_order: m.sort_order ?? 0, is_active: !!m.is_active,
        };
        this.preview = { image: m.image_link, qr_image: m.qr_image_link };
      },
    },
  },
  methods: {
    onImage(event, field) {
      const file = event.target.files[0];
      if (!file) return;
      this.files[field] = file;
      this.preview[field] = URL.createObjectURL(file);
    },
    buildFormData() {
      const fd = new FormData();
      const f = this.form;
      fd.append('name', f.name.trim());
      fd.append('account_name', f.account_name ?? '');
      fd.append('account_number', f.account_number ?? '');
      fd.append('iban', f.iban ?? '');
      fd.append('instructions', f.instructions ?? '');
      fd.append('sort_order', f.sort_order || 0);
      fd.append('is_active', f.is_active ? 1 : 0);
      if (this.files.image) fd.append('image', this.files.image);
      if (this.files.qr_image) fd.append('qr_image', this.files.qr_image);
      return fd;
    },
    async save() {
      try {
        const fd = this.buildFormData();
        if (this.method) await this.store.updateMethod(this.method.id, fd);
        else await this.store.createMethod(fd);
        await Swal.fire({ icon: 'success', title: 'تم الحفظ', timer: 1200, showConfirmButton: false });
        this.$router.push('/payment-methods');
      } catch (e) {
        Swal.fire({ icon: 'error', title: apiError(e), timer: 2500, showConfirmButton: false });
      }
    },
  },
};
</script>

<style scoped>
.settings-row {
  display: flex; align-items: center; justify-content: space-between; gap: 1rem; cursor: pointer;
}
.settings-row__text { display: flex; flex-direction: column; gap: 0.1875rem; }
.settings-row__title { font-size: 0.875rem; font-weight: 600; color: var(--text-primary); }
.settings-row__desc { font-size: 0.75rem; color: var(--text-secondary); }
</style>
